import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Dropdown Cantieri raggruppati per cliente, con ricerca e filtro per cliente.
 *
 * Props:
 *  - cantieri: array completo dei cantieri (contiene `id`, `name`, `client_name`, `cliente_id`, `address`)
 *  - value: id del cantiere selezionato ('' = nessuno)
 *  - onChange: (nuovoValue) => void
 *  - clienteId: opzionale — se popolato, filtra i cantieri mostrando SOLO quelli di quel cliente
 *  - clienteNome: opzionale — fallback per legacy (cantieri senza `cliente_id` ma con `client_name`)
 *  - placeholder: testo del placeholder
 *  - dataTestId
 *  - allowNone: se true, mostra opzione "Nessun cantiere"
 *  - disabled: disabilita il trigger
 */
export const CantiereSelectByCliente = ({
  cantieri = [],
  value = '',
  onChange,
  clienteId,
  clienteNome,
  placeholder = 'Seleziona cantiere…',
  dataTestId = 'cantiere-select',
  allowNone = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  // Filtro per cliente (se richiesto)
  const filteredByCliente = useMemo(() => {
    if (!clienteId && !clienteNome) return cantieri;
    const cid = String(clienteId || '').trim();
    const cnm = String(clienteNome || '').trim().toLowerCase();
    return cantieri.filter(c => {
      const cci = String(c.cliente_id || '').trim();
      const ccn = String(c.client_name || '').trim().toLowerCase();
      if (cid && cci === cid) return true;
      if (cnm && ccn === cnm) return true;
      return false;
    });
  }, [cantieri, clienteId, clienteNome]);

  // Raggruppa per client_name (fallback "Senza cliente")
  const gruppi = useMemo(() => {
    const map = new Map();
    for (const c of filteredByCliente) {
      const key = String(c.client_name || '').trim() || '— Senza cliente —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
      .map(([nome, items]) => ({ nome, items: items.sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), 'it', { sensitivity: 'base' })) }));
  }, [filteredByCliente]);

  const selected = useMemo(() => cantieri.find(c => c.id === value), [cantieri, value]);
  const isFiltered = Boolean(clienteId || clienteNome);
  const totFilteredCount = filteredByCliente.length;
  const totAllCount = cantieri.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !selected && 'text-slate-400')}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate">
              {selected ? (
                <>
                  {selected.name}
                  {selected.client_name && (
                    <span className="text-slate-400"> · {selected.client_name}</span>
                  )}
                </>
              ) : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cerca per nome cantiere o cliente…" data-testid={`${dataTestId}-search`} />
          <CommandList>
            {isFiltered && totFilteredCount === 0 ? (
              <CommandEmpty>
                <div className="px-3 py-4 text-sm">
                  Nessun cantiere per questo cliente ({totAllCount} totali nel sistema).
                </div>
              </CommandEmpty>
            ) : (
              <CommandEmpty>Nessun cantiere trovato.</CommandEmpty>
            )}
            {allowNone && (
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => { onChange && onChange(''); setOpen(false); }}
                  data-testid={`${dataTestId}-none`}
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  <span className="text-slate-500 italic">Nessun cantiere</span>
                </CommandItem>
              </CommandGroup>
            )}
            {gruppi.map(g => (
              <CommandGroup key={g.nome} heading={g.nome}>
                {g.items.map(c => (
                  <CommandItem
                    key={c.id}
                    // cmdk usa `value` per il match della ricerca
                    value={`${c.name} ${g.nome} ${c.address || ''}`}
                    onSelect={() => { onChange && onChange(c.id); setOpen(false); }}
                    data-testid={`${dataTestId}-opt-${c.id}`}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{c.name}</span>
                      {c.address && <span className="text-[11px] text-slate-400 truncate">{c.address}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          {isFiltered && totFilteredCount > 0 && totFilteredCount < totAllCount && (
            <div className="border-t px-3 py-1.5 text-[11px] text-slate-500 bg-slate-50">
              Mostrando {totFilteredCount} di {totAllCount} cantieri (filtro cliente attivo).
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CantiereSelectByCliente;
